import { useEffect } from 'react';
import logger from 'lc-logger';
import { Grid } from '@ui-propulsion/react-components';
import appConfig from 'lc-app-config';
import HelpCenterService from '../../../services/help-center';
import styles from '../../../styles/main.module.scss';
import helpStyles from '../../../styles/help.module.scss';
import csrf from '../../../services/helpers/csrf';
import render404Page from '../../../services/helpers/render404Page';
import PostBody from '../../../components/postBody/postBody';
import Breadcumbs from '../../../components/breadcrumbs/breadcrumbs';
import HelpTitle from '../../../components/helpTitle/helpTitle';
import ArticleList from '../../../components/articleList/articleList';
import GlossaryHubPrequal from '../../../components/glossaryHubPrequal/glossaryHubPrequal';
import SgiCalculator from '../../../components/sgiCalculator/sgiCalculator';
import {
  getOptimizelyUserExperiments,
  getUrlString,
} from '../../../services/helpers/common';
import WebsiteComponent from '../../../components/websiteComponent/websiteComponent';
import trackHeapEvent from '../../../services/helpers/trackHeapEvent';
import LcResources from '../../../components/lcResources/lcResources';

export default function Page({
  article,
  relatedQuestions,
  components,
  prequal,
  lcResources,
  calculator,
  breadcrumbs,
  experiments,
}) {
  useEffect(() => {
    trackHeapEvent(
      {
        Title: 'Page Load',
        Page: `Help - ${article.title}`,
      },
      `CMS_Unification`,
      experiments
    );
  }, [article, experiments]);
  const isSideBarActive = prequal || calculator;
  return (
    <main className={styles.main}>
      <div className={styles.container}>
        <div
          className={styles.section}
          itemScope
          itemType="https://schema.org/FAQPage"
        >
          <Grid container>
            <Grid
              item
              xs={12}
              md={8}
              className={isSideBarActive ? helpStyles.articleLeft : ''}
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
              itemScope="true"
            >
              <div className={helpStyles.breadcrumbs}>
                <Breadcumbs links={breadcrumbs} />
              </div>
              <div className={helpStyles.articleTitle}>
                <HelpTitle
                  {...{
                    title: article.title,
                    titleIcon: article.titleIcon,
                    publishDate: article.publishDate,
                    postContent: article.content,
                  }}
                />
              </div>
              <div
                className={helpStyles.postBody}
                itemType="https://schema.org/Answer"
                itemProp="acceptedAnswer"
                itemScope="true"
              >
                <PostBody
                  postContent={article.content}
                  showShare={false}
                  className="helpCenter"
                  contentType="Help"
                />
              </div>
              <div className={helpStyles.articleList}>
                <ArticleList
                  moduleSplit="100%"
                  modules={[
                    {
                      headline: `Additional ${article.subCategory} FAQ`,
                      items: relatedQuestions,
                    },
                  ]}
                />
              </div>
            </Grid>
            {isSideBarActive && (
              <Grid item xs={12} md={4}>
                <div className={helpStyles.prequal}>
                  {prequal && <GlossaryHubPrequal {...prequal} />}
                </div>

                <div className={helpStyles.sgiCalculator}>
                  <SgiCalculator {...calculator} />
                </div>
              </Grid>
            )}
          </Grid>
        </div>
      </div>
      <div className={styles.landingPage}>
        <div className={styles.components}>
          {components.map((component, index) => {
            const key = `${component.type}-${index}`;

            return (
              <div key={key}>
                <WebsiteComponent {...component} />
              </div>
            );
          })}
        </div>
      </div>
      {lcResources && (
        <div
          className={`${styles.container} ${helpStyles.lcResourcesContainer}`}
          id="Resources"
        >
          <div className={styles.section}>
            <LcResources
              title="Happen Bank Resources"
              lcResources={lcResources}
            />
          </div>
        </div>
      )}
    </main>
  );
}

export async function getServerSideProps(context) {
  const { category, slug } = context.params;
  const userVariation = getOptimizelyUserExperiments(context.req.headers);

  const helpCenterService = new HelpCenterService(context.preview);

  const props = await helpCenterService.getArticlePageBySlug(
    category,
    slug,
    userVariation
  );
  if (!props) {
    logger.info(
      {
        statusCode: 404,
        slug,
      },
      `LC-WEBSITE: Page not found for Slug`
    );
    return render404Page(context);
  }
  logger.info(
    {
      statusCode: 200,
      slug,
    },
    `LC-WEBSITE: Generating page for Slug`
  );

  const breadcrumbs = [{ label: 'Help Center', link: '/help' }];
  if (props.article?.category) {
    breadcrumbs.push({
      label: props.article.category,
      link: `/help/${getUrlString(props.article.category)}`,
    });
  }

  const spaceId = appConfig.get('CONTENTFUL_SPACE_ID');
  const csrfToken = await csrf(context.req, context.res);
  return {
    props: {
      ...props,
      breadcrumbs,
      isPreview: !!context.preview,
      spaceId,
      csrfToken,
      experiments: Object.fromEntries(userVariation),
    },
  };
}
